import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { ICourseSummary, IEventSummary, IDog } from '@eagerdog/interfaces';
import { Constants, DOG_OWNER_PREMIUM_ENABLED } from '@eagerdog/constants';

import { apiService} from "src/services/api.service";
import { helperService } from 'src/services/helper.service';
import { localStorageService } from 'src/services/local-storage.service';

import { Modal, useModal } from "src/components/Modal/Modal";
import Icon from "src/components/Icon/Icon";
import { SectionIcon, SectionIconName } from "src/components/SectionSidebar/SectionSidebar";
import Button from 'src/components/Button/Button';
import Wallet from "./components/Wallet/Wallet";
import EventForm from '../StepForm/EventForm/EventForm';
import CreateCourse from 'src/routes/Courses/CreateCourse/CreateCourse';
import { toast } from "src/components/Toast/ToastManager";
import DogForm from '../StepForm/DogForm/DogForm';
import Congrats from "src/components/Congrats/Congrats";
import StripeOnboardingModal from '../StripeOnboardingModal/StripeOnboardingModal';

import styles from './UserMenu.module.scss';

interface IProps {
  className?: string
}

interface IUserMenuItem {
  label: string,
  icon: string,
  sectionIcon?: SectionIconName,
  url?: string,
  external?: boolean,
  action?(): void
}

const UserMenu: React.FC<IProps> = (props) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [user, setUser] = useState(helperService.getUser());
  const [club, setClub] = useState(helperService.getClub());

  const [capacities, setCapacities] = useState<any[]>(helperService.getCapacities());

  const { isShown, toggle } = useModal();
  const navigate = useNavigate();
  const location = useLocation();

  const [modalContent, setModalContent] = useState<string>();
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [wallet, setWallet] = useState<any>();
  const [newEventUrl, setNewEventUrl] = useState<string>("");

  // Listen for subscription updates to refresh user data
  useEffect(() => {
    const handleUserDataUpdate = () => {
      const freshUser = helperService.getUser();
      const freshClub = helperService.getClub();
      const freshCapacities = helperService.getCapacities();
      setUser(freshUser);
      setClub(freshClub);
      setCapacities(freshCapacities);
    };
    
    window.addEventListener('userDataUpdated', handleUserDataUpdate);
    
    return () => {
      window.removeEventListener('userDataUpdated', handleUserDataUpdate);
    };
  }, []);

  // Derive from user's has_dog_profile_access flag
  const hasActiveSubscription = !DOG_OWNER_PREMIUM_ENABLED || (user.has_dog_profile_access ?? false);
  
  const ownerMenuItems: IUserMenuItem[] = [
    {
      label: "My Dogs",
      icon: "paw",
      sectionIcon: "paw",
      url: "/account?t=0"
    }, {
      label: "My Clubs",
      icon: "heart",
      sectionIcon: "registrations",
      url: "/account?t=3"
    }, {
      label: "Entries",
      icon: "trophy",
      sectionIcon: "ticket",
      url: "/account?t=1"
    }, {
      label: "Check-In & Results",
      icon: "eye",
      sectionIcon: "results",
      url: "/account?t=2"
    },
    ...(user.is_trusted_secretary
      ? [{
          label: "Trusted Secretary",
          icon: "star",
          url: "/trusted-secretary"
        } as IUserMenuItem]
      : []),
    {
      label: "Account Settings",
      icon: "cog",
      sectionIcon: "settings",
      url: "/account?t=5"
    }
  ];

  const menuItems:IUserMenuItem[] = user.user_type === Constants.user_type.club_manager ? [
    {
      label: "Create an Event",
      icon: "trophy",
      action: async () => {
        // Check Stripe status before opening modal
        if (club._id) {
          try {
            const status = await apiService.getAccountStatus(club._id);
            if (status.onboarding_status !== "requirements_met" || !status.charges_enabled) {
              setModalContent("stripeOnboarding");
              toggle();
              return;
            }
          } catch (err) {
            console.error("Failed to fetch account status:", err);
          }
        }
        setModalContent("eventForm");
        toggle();
      }
    }, {
      label: "Create a Course",
      icon: "school",
      action: async () => {
        // Check Stripe status before opening modal
        if (club._id) {
          try {
            const status = await apiService.getAccountStatus(club._id);
            if (status.onboarding_status !== "requirements_met" || !status.charges_enabled) {
              setModalContent("stripeOnboarding");
              toggle();
              return;
            }
          } catch (err) {
            console.error("Failed to fetch account status:", err);
          }
        }
        setModalContent("createCourse");
        toggle();
      }
    }
  ] : user.user_type === Constants.user_type.eagerdog_admin ? [
    {
      label: "Admin",
      icon: "cog",
      url: "/admin"
    }, {
      label: "Account Settings",
      icon: "cog",
      url: "/account?t=4"
    }
  ] : ownerMenuItems;

  const helpMenuItems:IUserMenuItem[] = [
    {
      label: "Help Guides",
      icon: "help",
      url: "https://help.eagerdog.com/en/",
      external: true
    },
    {
      label: "Facebook Community",
      icon: "facebook",
      url: "https://www.facebook.com/groups/eagerdog",
      external: true
    }
  ];

  const clubMenuItems:IUserMenuItem[] = [
    {
      label: "Contacts",
      icon: "members",
      url: "/k/" + club._id + "/manage/members"
    }, {
      label: "Points",
      icon: "points",
      url: "/k/" + club._id + "/manage/points"
    }, {
      label: "Balances",
      icon: "balances",
      url: "/k/" + club._id + "/manage/balances"
    },
    {
      label: "Settings",
      icon: "cog",
      url: "/k/" + club._id + "/manage/settings"
    }
  ];

  const logout = () => {
    window.location.href = "/";
  }

  const isLoggedIn = () => {
    return user._id !== "" && helperService.isLoggedIn();
  }

  const isClubAdmin = () => {
    return (capacities.length > 0 || user.user_type === Constants.user_type.club_manager);
  }

  const toggleMenu = (value?: boolean) => {
    setShowMenu(value ? value : !showMenu);
  }

  // Handle new event creation redirect (show congratulations modal)
  const searchParams = new URLSearchParams(location.search);
  const newEvent = searchParams.get("e");

  useEffect(() => {
    if (newEvent !== null) {
      setNewEventUrl(newEvent);
      setModalContent("congratsModal");
      // Clear the query parameter from URL
      navigate(location.pathname, { replace: true });
      toggle();
    }
  }, [newEvent, navigate, location.pathname, toggle]);

  // Close modal when navigating away from current page
  useEffect(() => {
    if (isShown && modalContent === "congratsModal") {
      return () => {
        // Cleanup: close modal if it's still open when location changes
        if (isShown) {
          toggle();
        }
      };
    }
  }, [location.pathname, isShown, modalContent, toggle]);

  const getModalContent = () => {
    switch (modalContent) {
      case "congratsModal":
        const congratsUrl = `/e/${newEventUrl}/?s=1&admin=${club._id}`;
        return <Congrats 
          text="You made an event! Now let's create some shows for your event." 
          url={congratsUrl} 
          urlText="Create Shows"
        />;
      case "stripeOnboarding":
        return (
          <StripeOnboardingModal
            clubId={club._id}
            onCancel={() => {
              toggle();
              setModalContent("");
            }}
          />
        );
      case "eventForm":
        return <EventForm
          clubNumber={club?.club_number as string | undefined}
          clubId={club ? club._id : ""}
          onFinish={(event: IEventSummary) => {
            setClub(helperService.getClub());
            navigate(`${location.pathname}?e=${event.handle}`, { replace: true });
            toggle();
          }}
        />;
      case "createCourse":
        return <CreateCourse
          onSubmit={(course: ICourseSummary) => {
            toggle();
            navigate("/c/" + course.handle);
          }}
        />;
      case "walletModal":
        return <Wallet
          wallet={wallet}
        />;
      case "dogForm":
        return <DogForm
          onFinish={(dog: IDog) => {
            toggle();

            toast.show({
              title: "Add a Dog",
              content: dog.call_name + " successfully added to your account!",
              duration: 10000,
              type: "success"
            });
          }}
        />;
    }

    return <></>;
  }

  useEffect(() => {
    if (!loaded) {
      // user._id/user_type alone only reflect a stale localStorage snapshot -
      // isLoggedIn() also confirms there's an access token, so we don't fire
      // authenticated calls (and risk a forced /logout redirect) for visitors
      // whose local session has actually expired or was never established.
      if (user.user_type === Constants.user_type.owner && helperService.isLoggedIn()) {
        apiService.getUserWallet().then((walletResponse) => {
          setWallet(walletResponse);
        });
        
        // Refresh user data to get latest has_dog_profile_access status
        apiService.whoAmI().then((freshUser) => {
          localStorageService.setItem(localStorageService.USER, JSON.stringify(freshUser));
          setUser(freshUser);
        }).catch(() => {
          // Keep existing user data on error
        });
      }

      // Refresh capacities from API to ensure they're up to date
      if (user._id && helperService.isLoggedIn()) {
        apiService.getUserCapacities().then((capacitiesResponse) => {
          localStorageService.setItem(localStorageService.CAPACITIES, JSON.stringify(capacitiesResponse.capacities));
          setCapacities(capacitiesResponse.capacities);
          setLoaded(true);
        }).catch(() => {
          setLoaded(true);
        });
      } else {
      setLoaded(true);
      }
    }
  }, [loaded, user.user_type, user._id]);

  const headerPhotoUrl = typeof user.image_url === "string" ? user.image_url : (user.image_url as { value?: string } | undefined)?.value;

  return(
    <div className={`${styles.UserMenu} ${props.className ? styles[props.className] : ""}`}>
      {!isLoggedIn() && <ul className={styles.loginMenu}>
        <li className={styles.learnMore}><a href="https://about.eagerdog.com">Learn More</a></li>
        <li><Link to="/login">Login</Link></li>
      </ul>}
      {isLoggedIn() && <div className={styles.portraitWrap}>
        <div className={styles.portrait}
          onMouseEnter={() => { toggleMenu(true); }}
          onMouseLeave={() => { toggleMenu(false); }}
        >
          <div className={styles.iconWrap}>
            {headerPhotoUrl
              ? <img className={styles.userPhoto} src={headerPhotoUrl} alt={user.display_name} />
              : <Icon className={styles.userIcon} type="user" />}
          </div>
          {showMenu && <div className={styles.menu}>
            <div className={styles.userDetails}>
              <span className={styles.userName}>{club._id !== "" ? club.club_name : user.display_name}</span>
              <span className={styles.userEmail}>{user.parent_email ? user.parent_email : user.email}</span>
              {club._id !== "" && <Link to={"/k/" + club._id} onClick={() => { toggleMenu(false); }} className={styles.userType}>View Club Page</Link>}
              {club._id === "" && wallet && wallet.total_points !== undefined && <span className={styles.userType}>{(wallet.total_points).toLocaleString('en-US')} pts · ${(wallet.total_points / 1000).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <div onClick={() => { setModalContent("walletModal"); toggle(); toggleMenu(false); }} className={styles.walletLink}>(view wallet)</div></span>}
            </div>
            {/*[Constants.user_type.owner, Constants.user_type.club_manager].includes(user.user_type) && !hasActiveSubscription && (
              <Link
                to="/premium" 
                className={styles.premiumCta}
                onClick={() => { toggleMenu(false); }}
              >
                <div className={styles.premiumIcon}>
                  <Icon type="star" className={styles.orange} />
                </div>
                <div className={styles.premiumContent}>
                  <span className={styles.premiumTitle}>Upgrade to Premium</span>
                  <span className={styles.premiumText}>
                    {user.user_type === Constants.user_type.club_manager 
                      ? "Premium pricing and advanced tools"
                      : "Enhanced profiles & title tracking"}
                  </span>
                </div>
              </Link>
            )*/}
            <ul>
              {isClubAdmin() && user.user_type !== Constants.user_type.eagerdog_admin && <li className={`${styles.category} ${styles.first}`}>{user.user_type === Constants.user_type.club_manager ? "Create" : "Account Management"}</li>}
              {menuItems.map((item: IUserMenuItem, index: number) => {
                return (
                  <li className={item.url ? styles.withUrl : undefined} key={index} onClick={(e) => {
                      if( item.action) {
                        item.action();
                      }

                      toggleMenu(false);
                    }
                  }>
                    {item.url ?
                    <Link to={item.url}>{item.sectionIcon ? <span className={styles.sectionIcon}><SectionIcon name={item.sectionIcon} /></span> : <Icon className={styles.listIcon} type={item.icon} />}<span>{item.label}</span></Link> : 
                    <>{item.sectionIcon ? <span className={styles.sectionIcon}><SectionIcon name={item.sectionIcon} /></span> : <Icon className={styles.listIcon} type={item.icon} />}<span>{item.label}</span></>}
                  </li>
                );
              })}
              <li className={styles.category}>Help</li>
              {helpMenuItems.map((item: IUserMenuItem, index: number) => {
                return (
                  <li key={index} className={styles.withUrl} onClick={() => { toggleMenu(false); }}>
                    {item.external ? 
                      <a href={item.url} target="_blank" rel="noopener noreferrer">
                        <Icon className={styles.listIcon} type={item.icon} /><span>{item.label}</span>
                      </a> :
                      <Link to={item.url || "/"}>
                        <Icon className={styles.listIcon} type={item.icon} /><span>{item.label}</span>
                      </Link>
                    }
                  </li>
                );
              })}
              {isClubAdmin() && (() => {
                const adminClubs = capacities.filter((capacity: any) => capacity.capacities.includes(Constants.capacity_type.admin));
                const hasAdminClubs = adminClubs.length > 0;
                const isClubManager = user.user_type === Constants.user_type.club_manager;
                
                // Show "Club Management" section if:
                // 1. User is a club_manager (always show for them)
                // 2. OR user is a dog owner with admin capacity for at least one club
                const showClubManagementSection = isClubManager || hasAdminClubs;
                
                return <>
                  {showClubManagementSection && <li className={styles.category}>Club Management</li>}
                  {adminClubs.map((capacity: any, index: number) => {
                    return (
                      <li key={index} className={styles.capacityItem} onClick={() => { toggleMenu(false); }}>
                        <Link to={"/k/" + capacity.club_id}>
                          <div className={!capacity.logo_url ? `${styles.clubPortrait} ${styles.noLogo}` : styles.clubPortrait}>{!capacity.logo_url && <span className={styles.initial}>{capacity.club_name.charAt(0)}</span>}{capacity.logo_url && <img alt="Club Logo" src={capacity.logo_url} />}</div>
                          <span>{capacity.club_name}</span>
                        </Link>
                      </li>
                    );
                  })}
                  {(isClubManager && club._id !== "") && <>
                    {clubMenuItems.map((item: IUserMenuItem, index: number) => {
                      return <li key={index} className={item.url ? styles.withUrl : undefined} onClick={() => { toggleMenu(false); }}>
                        <Link to={item.url || "/"}>
                          <Icon className={styles.listIcon} type={item.icon} /><span>{item.label}</span>
                        </Link>
                      </li>;
                    })}
                  </>}
                </>;
              })()}
              <li className={styles.logout}>
                <Button onClick={() => { helperService.logout(logout); }} className={styles.logoutWrap}>Logout</Button>
              </li>
            </ul>
          </div>}
        </div>
      </div>}
      <Modal 
        isShown={isShown}
        hide={toggle}
        className={modalContent === "stripeOnboarding" ? "stripeOnboardingModal" : modalContent}
        modalContent={getModalContent()}
      />
    </div>
  );
}

export default UserMenu;